import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, Inject, InjectionToken, NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { AppRoutingModule } from './app.routes';
import { HTTP_INTERCEPTORS, HttpContextToken, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpErrorService } from './services/http-error.service';
import { ToastrModule } from 'ngx-toastr';
import { CoreModule } from './core/core.module';
import { CommonModule } from '@angular/common';
import { ConfigService } from './services/config/config.service';
import { CustomerManagementService } from './services/customer-management/customer-management.service';
import { ConfirmDialogComponentDialog } from './confirm-dialog/confirm-dialog.component';
import { OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { storageFactory } from './app-init/storage.init';
import { TokenInterceptor } from './interceptors/token-interceptor';
import { InformationDialogComponent } from './information-dialog/information-dialog/information-dialog.component';
import { AutoLogoutService } from './services/auto-logout.service';
import { AuditLogService } from './services/audit-log/audit-log.service';
import { CustomerFormComponent } from './customer-form/customer-form.component';
import { CustomerAddPageComponent } from './customer-add-page/customer-add-page.component';
import { CustomerEditPageComponent } from './customer-edit-page/customer-edit-page.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LookupManagerComponent } from './lookup-manager/lookup-manager.component';
import { NotificationSetupModalComponent } from './notification-setup-modal/notification-setup-modal.component';
import { HomeModule } from './home/home-module/home.module';
import { CustomerUsageComponent } from './customer-usage/customer-usage.component';
import { MetricsDashboardComponent } from './metrics-dashboard/metrics-dashboard.component';
import { TenantStackBarChartComponent } from './metrics-dashboard/tenant-stack-bar-chart/tenant-stack-bar-chart.component';
import { CountCardComponent } from './customer-usage/count-card/count-card.component';
import { PercentageProgressComponent } from './customer-usage/percentage-progress/percentage-progress.component';
import { IncidentsCreatedChartComponent } from './customer-usage/incidents-created-chart/incidents-created-chart.component';
import { DateSelectorComponent } from './customer-usage/date-selector/date-selector.component';
import { IncidentsSharedChartComponent } from './customer-usage/incidents-shared-chart/incidents-shared-chart.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { applyPieLabelSpacing } from 'src/util/pie-label-layout';
import { UploadStatusesChartComponent } from './customer-usage/upload-statuses-chart/upload-statuses-chart.component';
import { CountsChartComponent } from './customer-usage/counts-chart/counts-chart.component';
import { DownloadsChartComponent } from './customer-usage/downloads-chart/downloads-chart.component';
import { StreamingGraphComponent } from './customer-usage/streaming-graph/streaming-graph.component';
import { RedactionGraphComponent } from './customer-usage/redaction-graph/redaction-graph.component';
import { UsersChartComponent } from './customer-usage/users-chart/users-chart.component';
import { UserAuditLogPageComponent } from './user-audit-log-page/user-audit-log-page.component';
import { AuditLoggingTable } from './user-audit-log-page/audit-logging-table/audit-logging-table.component';
import { DateTimeModule } from '@synx/date-time';
import { TableModule } from '@synx/table';
import { Culture, TranslatePipe, TranslateService } from '@synx/translate';
import { FormComponentsModule } from '@synx/form-components';
import { initAuth, initConfig } from '@synx-cloud/app-init';
import { AppTokenService, BaseConfigService, BasePermissionsService } from '@synx-cloud/services';
import { PermissionsService } from './services/permissions/permissions.service';

export const GENERIC_ERROR_SUPPRESSION_TOKEN = new InjectionToken<HttpContextToken<boolean>>('genericErrorSuppressionKey');

@NgModule({ 
    declarations: [ 
        AppComponent,
        ConfirmDialogComponentDialog,
        InformationDialogComponent,
        CustomerFormComponent,
        CustomerAddPageComponent,
        CustomerEditPageComponent,
        LookupManagerComponent,
        NotificationSetupModalComponent,
        CustomerUsageComponent,
        MetricsDashboardComponent,
        TenantStackBarChartComponent,
        CountCardComponent,
        PercentageProgressComponent,
        IncidentsCreatedChartComponent,
        DateSelectorComponent,
        IncidentsSharedChartComponent,
        UploadStatusesChartComponent,
        CountsChartComponent,
        DownloadsChartComponent,
        StreamingGraphComponent,
        RedactionGraphComponent,
        UsersChartComponent,
        UserAuditLogPageComponent,
        AuditLoggingTable
    ],
    bootstrap: [AppComponent], 
    imports: [
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        BrowserModule,
        RouterModule,
        CommonModule,
        HomeModule,
        CoreModule,
        ToastrModule.forRoot({
            timeOut: 10000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: true,
        }),
        OAuthModule.forRoot({
            resourceServer: {
                sendAccessToken: true,
            },
        }),
        NgxChartsModule,
        DateTimeModule,
        TranslatePipe,
        FormComponentsModule,
        TableModule
        //IconsModule,
        //IconTileComponent,
    ], 
    providers: [
        // {
        //     provide: IconProvider, // from @synx/icons
        //     useFactory: () => {
        //         const p = new IconProvider();
        //         p.useDefaults();
        //         p.add('my-new-icon', `<path d="M450-450H200v-60h250v-250h60v250h250v60H510v250h-60v-250Z" fill="currentColor" />`, '0 -960 960 960');
        //         return p;
        //     }
        // },
        { provide: BaseConfigService, useExisting: ConfigService },
        { provide: BasePermissionsService, useExisting: PermissionsService },
        AppTokenService,
        CustomerManagementService,
        HttpErrorService,
        {
            provide: OAuthStorage,
            useFactory: storageFactory,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initConfig,
            multi: true,
            deps: [ConfigService],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (config, oauthService, auditLogService, appTokenService, permissionsService) => 
                initAuth(config, oauthService, auditLogService, appTokenService, permissionsService, []),
            multi: true,
            deps: [
                ConfigService,
                OAuthService,
                AuditLogService,
                AppTokenService,
                PermissionsService
            ],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        AutoLogoutService,
        TranslateService,
        { provide: GENERIC_ERROR_SUPPRESSION_TOKEN, useValue: new HttpContextToken<boolean>(() => false) },
        // {
        //   provide: ErrorHandler,
        //   useClass: ApmErrorHandler,
        //   deps: [Router],
        // },
        {
            provide: 'ICulture',
            useValue: new Culture('en-GB')
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
  constructor(
    private autoLogoutService: AutoLogoutService,
    private auditLogService: AuditLogService
  ) {
    // Outside pie labels are styled at 16px (styles.scss), wider than ngx-charts' built-in label
    // spacing allows for, so neighbouring labels would otherwise overlap.
    applyPieLabelSpacing();
  }
}
